<script setup>
import {ref, onMounted, onBeforeUnmount, useTemplateRef} from 'vue';
import {mdiCamera, mdiClose, mdiCloseCircle} from '@mdi/js';
import {pgettext} from "@/utils/translate";

const emit = defineEmits(['image', 'close']);

const videoRef = useTemplateRef('videoRef');
const canvasRef = useTemplateRef('canvasRef');
const stream = ref(null);
const isLoading = ref(true);
const isError = ref(false);
const capturedIndicator = ref(false);
let timeout = null;

// Initialize camera stream
async function initCamera() {
    try {
        isLoading.value = true;

        // Request camera access with preferred settings for mobile
        const constraints = {
            video: {
                facingMode: 'environment', // Use back camera by default
                width: {ideal: 1920},
                height: {ideal: 1080}
            }
        };

        stream.value = await navigator.mediaDevices.getUserMedia(constraints);

        // Connect the stream to the video element
        if (videoRef.value) {
            console.log('connected video');
            videoRef.value.srcObject = stream.value;
            await videoRef.value.play();
        }

        isLoading.value = false;
    } catch (error) {
        console.error('Error accessing camera:', error);
        isLoading.value = false;
        isError.value = true;
    }
}

// Capture image from video stream
function captureImage() {
    if (!videoRef.value || !canvasRef.value || isError.value) return;

    const video = videoRef.value;
    const canvas = canvasRef.value;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to the canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob (for upload compatibility)
    canvas.toBlob((blob) => {
        // Create a File object from the blob
        const file = new File([blob], `photo_${Date.now()}.jpg`, {type: 'image/jpeg'});

        // Emit the captured image
        emit('image', {file});
    }, 'image/jpeg', 0.95); // High quality JPEG

    capturedIndicator.value = true;
    if (timeout) {
        clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
        capturedIndicator.value = false;
    }, 3000);
}

// Close the camera
function closeCamera() {
    emit('close');
}

// Clean up resources when component is unmounted
function stopCamera() {
    if (stream.value) {
        stream.value.getTracks().forEach(track => track.stop());
        stream.value = null;
    }
}

onMounted(() => {
    initCamera();
});

onBeforeUnmount(() => {
    stopCamera();
});

const show = ref(true);
</script>

<template>
    <v-dialog v-model="show" fullscreen :scrim="false" :persistent="true">
        <v-card class="camera-container">
            <!-- Loading indicator -->
            <div v-if="isLoading" class="loading-container">
                <v-progress-circular indeterminate size="64"/>
                <p class="mt-4">{{ pgettext('message', 'Starting camera') }}</p>
            </div>

            <div v-else-if="isError" class="loading-container">
                <v-icon :icon="mdiCloseCircle" :size="64" />
                <p class="mt-4">{{ pgettext('label', 'Unable to access the device camera.') }}</p>
                <p class="mt-4">
                    <v-btn @click="initCamera()">
                        {{ pgettext('action', 'Retry') }}
                    </v-btn>
                </p>
            </div>

            <!-- Camera view -->
            <div class="camera-view">
                <video ref="videoRef" autoplay playsinline class="camera-video"></video>
                <canvas ref="canvasRef" style="display: none;"></canvas>

                <!-- Camera controls -->
                <div class="camera-controls">
                    <div v-if="capturedIndicator" class="camera-notification pa-4">
                        <v-alert type="success">
                            <strong>{{ pgettext('message', 'Photo taken and being uploaded to patient.') }}</strong>
                        </v-alert>
                    </div>

                    <v-btn
                        class="close-btn"
                        variant="text"
                        color="white"
                        @click="closeCamera">
                        <v-icon :icon="mdiClose" size="x-large"/>
                    </v-btn>

                    <v-btn
                        class="capture-btn"
                        icon
                        color="white"
                        rounded
                        :disabled="isError || isLoading"
                        @click="captureImage">
                        <v-icon :icon="mdiCamera" size="x-large"/>
                    </v-btn>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.camera-container {
    position: relative;
    height: 100%;
    background-color: #000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.loading-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    background-color: #000;
    z-index: 10;
}

.camera-view {
    position: relative;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

.camera-video {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.camera-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
    z-index: 100;
}

.camera-notification {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    color: white;
}

.close-btn {
    position: absolute;
    top: 20px;
    left: 20px;
}

.capture-btn {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.3);
    border: 4px solid white;
}

/* Mobile optimization */
@media screen and (max-width: 767px) {
    .camera-controls {
        padding: 16px;
    }

    .close-btn {
        top: 16px;
        left: 16px;
    }

    .capture-btn {
        width: 64px;
        height: 64px;
    }
}
</style>